import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { API_BASE_URL } from '../../config/api';
import { authHeaders } from '../../services/auth';
import DataTable from '../../components/ui/DataTable';
import Spinner from '../../components/ui/Spinner';
import ToastProvider, { useToast } from '../../components/ui/ToastProvider';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Modal from '../../components/ui/Modal';
import ImageUpload from '../../components/admin/ImageUpload';
import { adminTheme } from '../../theme-admin';
import { cache } from '../../utils/cache';

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${adminTheme.spacing.xl};
`;

const Title = styled.h2`
  font-family: ${adminTheme.fonts.heading};
  font-size: ${adminTheme.fontSizes['3xl']};
  font-weight: ${adminTheme.fontWeights.bold};
  color: ${adminTheme.colors.gray[900]};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${adminTheme.spacing.md};
`;

const CreateButton = styled.button`
  padding: ${adminTheme.spacing.md} ${adminTheme.spacing.xl};
  background: ${adminTheme.gradients.primary};
  color: white;
  border: none;
  border-radius: ${adminTheme.borderRadius.lg};
  font-weight: ${adminTheme.fontWeights.bold};
  cursor: pointer;
  box-shadow: ${adminTheme.shadows.md};
  display: flex;
  align-items: center;
  gap: ${adminTheme.spacing.sm};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${adminTheme.shadows.lg};
  }
`;

const TableWrapper = styled.div`
  background: ${adminTheme.colors.background.primary};
  border: 1px solid ${adminTheme.colors.gray[200]};
  border-radius: ${adminTheme.borderRadius['2xl']};
  padding: ${adminTheme.spacing.xl};
  box-shadow: ${adminTheme.shadows.md};
`;

const ModalForm = styled.div`
  display: grid;
  gap: ${adminTheme.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${adminTheme.spacing.sm};
`;

const Label = styled.label`
  font-size: ${adminTheme.fontSizes.sm};
  font-weight: ${adminTheme.fontWeights.semibold};
  color: ${adminTheme.colors.gray[700]};
`;

const Input = styled.input`
  padding: ${adminTheme.spacing.md};
  border: 1px solid ${adminTheme.colors.gray[300]};
  border-radius: ${adminTheme.borderRadius.lg};
  font-size: ${adminTheme.fontSizes.base};
  color: ${adminTheme.colors.gray[900]};
  background-color: ${adminTheme.colors.background.primary};

  &:focus {
    outline: none;
    border-color: ${adminTheme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${adminTheme.colors.primary[100]};
  }

  &::placeholder {
    color: ${adminTheme.colors.gray[400]};
  }
`;

const ActionButton = styled.button`
  padding: ${adminTheme.spacing.sm} ${adminTheme.spacing.md};
  border-radius: ${adminTheme.borderRadius.lg};
  font-weight: ${adminTheme.fontWeights.semibold};
  font-size: ${adminTheme.fontSizes.sm};
  cursor: pointer;
  border: 1px solid ${adminTheme.colors.gray[300]};
  background: ${adminTheme.colors.background.primary};
  margin-right: ${adminTheme.spacing.sm};

  &:hover { background: ${adminTheme.colors.gray[100]}; }
`;

const DeleteButton = styled(ActionButton)`
  border-color: ${adminTheme.colors.danger[300]};
  background: ${adminTheme.colors.danger[500]};
  color: white;

  &:hover { background: ${adminTheme.colors.danger[600]}; }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${adminTheme.spacing.sm};
`;

const HelpText = styled.p`
  font-size: ${adminTheme.fontSizes.sm};
  color: ${adminTheme.colors.gray[600]};
  margin: ${adminTheme.spacing.xs} 0 0 0;
  font-style: italic;
`;

const LogoPreview = styled.div`
  margin-top: ${adminTheme.spacing.md};
  padding: ${adminTheme.spacing.lg};
  background: ${adminTheme.colors.gray[50]};
  border-radius: ${adminTheme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  border: 1px dashed ${adminTheme.colors.gray[300]};

  img {
    max-height: 80px;
    max-width: 200px;
    object-fit: contain;
  }
`;

function PageBody() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const toast = useToast();

  const getInitialForm = () => ({
    name: '',
    logo: '',
    url: '',
    order: 0,
    isActive: true,
  });
  const [editForm, setEditForm] = useState(getInitialForm());

  const load = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh) {
      const cached = cache.get('partners-list-admin');
      if (cached) { setItems(cached); return; }
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/partners/all`, { headers: authHeaders() });
      const data = await res.json();
      const partners = data.data?.partners || [];
      setItems(partners);
      cache.set('partners-list-admin', partners);
    } catch (_) {
      toast.error('Failed to load partners');
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setIsCreating(true); setEditing(null); setEditForm(getInitialForm()); };
  const openEdit = (item) => { setIsCreating(false); setEditing(item); setEditForm({ ...getInitialForm(), ...item }); };
  const closeModal = () => { setIsCreating(false); setEditing(null); setEditForm(getInitialForm()); };

  const savePartner = async () => {
    try {
      const url = isCreating ? `${API_BASE_URL}/partners` : `${API_BASE_URL}/partners/${editing._id}`;
      const method = isCreating ? 'POST' : 'PUT';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify(editForm) });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Operation failed');
      toast.success(isCreating ? 'Partner added' : 'Partner updated');
      cache.invalidate('partners-list-admin');
      closeModal();
      load(true);
    } catch (e) { toast.error(e.message || 'Save failed'); }
  };

  const onDelete = (id) => setConfirmId(id);
  const confirmDelete = async () => {
    const id = confirmId; setConfirmId(null);
    try {
      const res = await fetch(`${API_BASE_URL}/partners/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Partner deleted');
      cache.invalidate('partners-list-admin');
      load(true);
    } catch (_) { toast.error('Delete failed'); }
  };

  return (
    <div>
      <PageHeader>
        <Title>
          <span>🏢</span>
          Partners
        </Title>
        <CreateButton onClick={openCreate}><span>➕</span>Add Partner</CreateButton>
      </PageHeader>

      <TableWrapper>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <Spinner label="Loading partners..." />
          </div>
        ) : (
          <DataTable
            columns={[
              { key: 'logo', header: 'Logo', width: '1fr', render: (r) => r.logo ? (
                <img 
                  alt={r.name} 
                  src={r.logo} 
                  style={{ 
                    height: 50, 
                    maxWidth: 120,
                    objectFit: 'contain',
                    filter: 'grayscale(100%)',
                    transition: 'filter 0.3s'
                  }} 
                  onMouseEnter={(e) => e.target.style.filter = 'grayscale(0%)'}
                  onMouseLeave={(e) => e.target.style.filter = 'grayscale(100%)'}
                />
              ) : <span style={{ color: adminTheme.colors.gray[400] }}>No logo</span> },
              { key: 'name', header: 'Company', width: '1.5fr' },
              { key: 'url', header: 'URL', width: '2fr', render: (r) => r.url ? <a href={r.url} target="_blank" rel="noreferrer">{r.url}</a> : '—' },
              { key: 'order', header: 'Order', width: '0.5fr' },
              { key: 'isActive', header: 'Active', width: '0.6fr', render: (r) => r.isActive ? 'Yes' : 'No' },
            ]}
            rows={items}
            actions={(r) => (
              <>
                <ActionButton onClick={() => openEdit(r)}>✏️ Edit</ActionButton>
                <DeleteButton onClick={() => onDelete(r._id)}>🗑️ Delete</DeleteButton>
              </>
            )}
          />
        )}
      </TableWrapper>

      <ConfirmDialog
        open={!!confirmId}
        onCancel={() => setConfirmId(null)}
        onConfirm={confirmDelete}
        title="Delete partner?"
        description="This action cannot be undone."
      />

      <Modal
        open={isCreating || !!editing}
        title={isCreating ? 'Add Partner' : 'Edit Partner'}
        onCancel={closeModal}
        onSave={savePartner}
        saveLabel={isCreating ? 'Create' : 'Save'}
      >
        <ModalForm>
          <FormGroup>
            <Label>Company Name *</Label>
            <Input
              placeholder="Acme Corp"
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              required
            />
          </FormGroup>

          <FormGroup>
            <ImageUpload
              label="Company Logo *"
              value={editForm.logo}
              onChange={(url) => setEditForm({ ...editForm, logo: url })}
              folder="partners"
            />
            <HelpText>
              💡 Upload a high-quality logo (PNG or SVG recommended). For best display, use transparent background. 
              Recommended size: 200x80px or similar aspect ratio.
            </HelpText>
            {editForm.logo && (
              <LogoPreview>
                <img src={editForm.logo} alt="Logo preview" />
              </LogoPreview>
            )}
          </FormGroup>

          <FormGroup>
            <Label>Company URL</Label>
            <Input
              placeholder="https://company.com"
              value={editForm.url}
              onChange={(e) => setEditForm({ ...editForm, url: e.target.value })}
            />
          </FormGroup>

          <FormGroup>
            <Label>Order</Label>
            <Input
              type="number"
              value={editForm.order}
              onChange={(e) => setEditForm({ ...editForm, order: parseInt(e.target.value || '0', 10) })}
            />
          </FormGroup>

          <CheckboxGroup>
            <input
              id="partner-active"
              type="checkbox"
              checked={!!editForm.isActive}
              onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
            />
            <Label htmlFor="partner-active">Active</Label>
          </CheckboxGroup>
        </ModalForm>
      </Modal>
    </div>
  );
}

export default function AdminPartners() {
  return (
    <ToastProvider>
      <PageBody />
    </ToastProvider>
  );
}
